import React, { useEffect, useState, createContext, useContext } from 'react';
import clientApi from 'clientApi';
import schedulerApi from 'schedulerApi';
import { initNewRelic } from 'monitoring/initNewRelic';
import { getDealerSubdomain } from 'utility/helpers/getDealerSubdomain';

export const ApiClientContext = createContext<{
  clientApi: typeof clientApi;
  schedulerApi: typeof schedulerApi;
  envTarget: string;
} | null>(null);

export default function ApiClientProvider({ children }: { children: React.ReactNode }) {
  const [apiConfig, setApiConfig] = useState<{
    apiUrl: string;
    envTarget: string;
  } | null>(null);
  const [hasLoadedConfig, setHasLoadedConfig] = useState(false);

  useEffect(() => {
    async function fetchApiConfig() {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/config.json`);
        const json = await response.json();

        setApiConfig(json);
        setHasLoadedConfig(true);

        // After the render gate: children don't mount until hasLoadedConfig is true, and
        // nothing paints before that (Suspense's spinner is nested inside this provider),
        // so initializing here keeps agent construction off the blank-screen path while
        // still installing it before any app XHR. Contained so a monitoring failure is
        // never mistaken for a config-fetch failure by the catch below.
        try {
          initNewRelic(json.apiUrl, json.envTarget, getDealerSubdomain());
        } catch (monitoringError) {
          console.error('Failed to initialize New Relic', monitoringError);
        }
      } catch (e) {
        setHasLoadedConfig(true);
      }
    }

    fetchApiConfig();
  }, []);

  useEffect(() => {
    if (hasLoadedConfig && apiConfig?.apiUrl) {
      clientApi._updateBaseUrl(apiConfig.apiUrl);
      schedulerApi.defaults.baseURL = apiConfig.apiUrl;
    }
  }, [hasLoadedConfig, apiConfig]);

  return (
    <ApiClientContext.Provider
      value={{
        clientApi,
        schedulerApi,
        envTarget: apiConfig?.envTarget || process.env.REACT_APP_ENVIRONMENT || 'development',
      }}
    >
      {hasLoadedConfig && children}
    </ApiClientContext.Provider>
  );
}

export const useApiClientContext = () => {
  const context = useContext(ApiClientContext);

  if (!context) {
    throw new Error('useApiClientContext must be used within ApiClientProvider');
  }

  return context;
};
